/**
 * Header `Create ▾` primary action menu.
 *
 * Opens the same create dialogs the Projects / Contacts / Inventory /
 * Manufacturer Settings list pages use (via `CreateEntityDialogsContext`),
 * rather than navigating to a route — `/projects/new` and `/contacts/new`
 * were never registered routes, so those items 404'd before this change.
 */
import React from 'react'
import { Boxes, ChevronDown, Factory, FolderPlus, Plus, UserPlus } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useCreateEntityDialogs } from '@/components/layout/CreateEntityDialogsContext'

interface CreateMenuProps {
  className?: string
}

export const CreateMenu = ({ className }: CreateMenuProps): JSX.Element => {
  const { openProject, openContact, openProduct, openManufacturer } = useCreateEntityDialogs()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={`h-9 px-3 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded inline-flex items-center gap-1.5 transition-colors duration-fast focus-visible:outline-none ${className ?? ''}`}
        aria-label="Create new"
      >
        <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
        <span>Create</span>
        <ChevronDown className="h-[14px] w-[14px] opacity-80" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Create
        </DropdownMenuLabel>
        <DropdownMenuItem onSelect={openProject} className="gap-2.5">
          <FolderPlus className="h-4 w-4 text-muted-foreground" />
          <span className="flex-1">New Project</span>
          <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded border bg-muted">N</kbd>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={openContact} className="gap-2.5">
          <UserPlus className="h-4 w-4 text-muted-foreground" />
          <span className="flex-1">New Contact</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={openProduct} className="gap-2.5">
          <Boxes className="h-4 w-4 text-muted-foreground" />
          <span className="flex-1">New Product</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={openManufacturer} className="gap-2.5">
          <Factory className="h-4 w-4 text-muted-foreground" />
          <span className="flex-1">New Manufacturer</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
